import color from "color";
import { DefaultTheme } from "styled-components";

export type VariantStyles = {
  basic: (textColor: string, theme: DefaultTheme) => string;
  bordered: (borderColor: string, theme: DefaultTheme) => string;
  filled: (background: string, theme: DefaultTheme) => string;
};

// One rule for every variant. The rest state is restated so a hover that
// lands on a disabled button changes nothing.
const disabledRule = (restBackground: string) => `
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: ${restBackground};
  }
`;

// How strongly a hover and a press mark a button: the tint behind a text or
// outlined button, the darkening of a fill. The contrast test measures text on
// both tints, so a text colour keeps AA while the pointer is on it.
export const HOVER_TINT = 0.1;
export const PRESSED_TINT = 0.16;

// Text and outlined buttons keep their text colour in every state; the hover
// and the press tint the ground behind it with that colour.
const tintStates = (textColor: string) => `
  &:hover {
    background-color: ${color(textColor).alpha(HOVER_TINT)};
  }

  &:active {
    background-color: ${color(textColor).alpha(PRESSED_TINT)};
  }

  ${disabledRule("transparent")}
`;

export const getTextColor = (textColor: string, _theme: DefaultTheme) => `
  color: ${textColor};

  ${tintStates(textColor)}
`;

// Black text on a light fill, the theme's filled text on a dark one.
export const getFilledTextColor = (background: string, theme: DefaultTheme) =>
  color(background).luminosity() > theme.buttonLightBgThreshold
    ? "#000"
    : theme.buttonFilledText;

// A pressed fill goes darker, so the text on it never loses contrast (a faded
// fill did). Near-black has nowhere darker to go and lifts instead.
export const shade = (background: string, amount: number) => {
  const fill = color(background);
  return fill.luminosity() < 0.02
    ? fill.mix(color("#fff"), amount * 2)
    : fill.darken(amount);
};

export const getBackgroundColor = (
  background: string,
  theme: DefaultTheme,
  textColor = getFilledTextColor(background, theme),
) => {
  const hasShadow = theme.buttonFilledShadow !== 'none';
  return `
  background-color: ${background};
  color: ${textColor};
  box-shadow: ${hasShadow ? `0 3px 10px 0 ${color(background).alpha(0.5)}` : 'none'};

  &:hover {
    color: ${textColor};
    background-color: ${shade(background, HOVER_TINT)};
    box-shadow: ${hasShadow ? `0 1px 6px 0 ${color(background).alpha(0.8)}` : 'none'};
  }

  &:active {
    background-color: ${shade(background, PRESSED_TINT)};
  }

  ${disabledRule(background)}
`;
};

// A hairline in the form-border grey with the text in the button colour; the
// hover tints the fill with that colour instead of fading the text.
export const getBorderColor = (textColor: string, theme: DefaultTheme) => `
  color: ${textColor};
  background-color: transparent;
  border: 1px solid ${theme.formBorder};

  ${tintStates(textColor)}
`;

export const variantStyles: VariantStyles = {
  basic: getTextColor,
  bordered: getBorderColor,
  filled: getBackgroundColor,
};
